import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserCog, Search, ShieldAlert, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only impersonation panel.
 *
 * Lists non-admin, active users and lets the admin start impersonation.
 * Also shows the recent impersonation audit log so admins can review
 * their own history (and any other admin's).
 *
 * The actual token swap + banner is handled by AuthContext +
 * ImpersonationBanner — this component is just the UI shell.
 */
export function ImpersonationPanel() {
  const { user, token, startImpersonation } = useAuth();
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const isAdmin = Array.isArray(user?.roles) && user.roles.includes("admin");

  const loadAll = async () => {
    try {
      setLoading(true);
      const [usersResp, logsResp] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${API}/admin/impersonation-logs?limit=50`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: { logs: [] } })),
      ]);
      setUsers(usersResp.data.users || []);
      setLogs(logsResp.data.logs || []);
    } catch (err) {
      toast.error("Failed to load users / logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && token) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, token]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        const roles = u.roles || [];
        // Policy: admins are not impersonatable.
        if (roles.includes("admin")) return false;
        if (u.active === false) return false;
        return true;
      })
      .filter((u) => {
        if (!q) return true;
        return (
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.roles || []).some((r) => r.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, search]);

  const handleImpersonate = async (targetUser) => {
    try {
      setBusyId(targetUser.id);
      const target = await startImpersonation(targetUser.id);
      toast.success(`Now impersonating ${target.name || target.email}`);
      // Route to their natural home page.
      const roles = target.roles || [];
      let dest = "/";
      if (roles.includes("facilitator")) dest = "/facilitator";
      else if (roles.includes("therapist")) dest = "/therapist";
      // Force full reload so every provider (WebSocket, Notification)
      // re-initializes against the new token cleanly.
      window.location.assign(dest);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to start impersonation"
      );
    } finally {
      setBusyId(null);
      setConfirmTarget(null);
    }
  };

  if (!isAdmin) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            Admin access is required to use impersonation.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy card */}
      <Card className="border-amber-300 bg-amber-50/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <ShieldAlert className="w-5 h-5 text-amber-700" />
            Impersonation policy
          </CardTitle>
          <CardDescription>
            When you impersonate, you sign in <em>as</em> the selected user
            for up to 30 minutes. All actions you take will be recorded under
            their account and this impersonation is logged. Admins cannot be
            impersonated. Deactivated users cannot be impersonated.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* User picker */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <UserCog className="w-5 h-5" /> Impersonate a user
          </CardTitle>
          <CardDescription>
            Search by name, email, or role. Only non-admin, active users are
            listed.
          </CardDescription>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="pl-9"
              data-testid="impersonation-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No users match your search.
            </p>
          ) : (
            <div
              className="divide-y max-h-[520px] overflow-y-auto"
              data-testid="impersonation-user-list"
            >
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between py-3 gap-3"
                  data-testid={`impersonation-user-row-${u.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {u.email}
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(u.roles || []).map((r) => (
                        <Badge key={r} variant="outline" className="text-[10px]">
                          {r}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmTarget(u)}
                    disabled={busyId === u.id}
                    data-testid={`impersonate-btn-${u.id}`}
                  >
                    {busyId === u.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserCog className="w-4 h-4 mr-2" />
                    )}
                    Impersonate
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent audit log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Clock className="w-5 h-5" /> Recent impersonation activity
          </CardTitle>
          <CardDescription>
            Every start and stop event is recorded for audit purposes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No impersonation activity yet.
            </p>
          ) : (
            <div className="divide-y max-h-[400px] overflow-y-auto text-sm">
              {logs.map((l) => (
                <div key={l.id} className="py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant={l.event === "start" ? "default" : "secondary"}
                      className="text-[10px]"
                    >
                      {l.event.toUpperCase()}
                    </Badge>
                    <span className="font-medium">{l.admin_name || l.admin_email}</span>
                    {l.event === "start" && (
                      <>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-medium">
                          {l.target_name || l.target_email}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({(l.target_roles || []).join(", ")})
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {l.created_at ? new Date(l.created_at).toLocaleString() : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(open) => !open && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="impersonation-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Impersonate this user?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be signed in as{" "}
              <strong>{confirmTarget?.name || confirmTarget?.email}</strong>{" "}
              for up to 30 minutes. Every action you take will be recorded
              under their account with an audit trail linking back to you.
              Use "Return to admin" from the orange banner to end
              impersonation at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impersonation-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTarget && handleImpersonate(confirmTarget)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="impersonation-confirm-btn"
            >
              Continue as this user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImpersonationPanel;
